<template>
  <!-- Validate only on submit: no field errors before the user has touched anything or while typing;
       the red messages appear only after a submit attempt. -->
  <Form :resolver="resolver" :initial-values="initialData" @submit="handleSubmit"
    :validate-on-value-update="false" :validate-on-blur="false" :validate-on-mount="false"
    class="space-y-6" ref="relationshipForm">

    <!-- On create, the user first chooses what to build: use a pre-defined type as-is, or create
         their own (optionally derived from an existing one). Each path maps to exactly one lineage
         field, so there is no shared/encoded value to decode later. Hidden when editing. -->
    <div v-if="!isEditMode" class="space-y-1">
      <label>O que você deseja criar?</label>
      <SelectButton v-model="mode" :options="modeOptions" optionLabel="label" optionValue="value" :allowEmpty="false" />
    </div>

    <!-- Pre-defined path: pick a pre-defined type; nothing else to fill in — name, description and
         entity types come from it. -->
    <template v-if="!isEditMode && mode === 'predefined'">
      <div class="space-y-1">
        <label for="config_type">Tipo pré-definido</label>
        <Select id="config_type" v-model="configId" :options="configTypes" optionLabel="name" optionValue="id"
          placeholder="Selecione um tipo pré-definido" fluid />
        <small>O nome, a descrição e os tipos de entidade serão herdados do tipo pré-definido.</small>
      </div>

      <div v-if="selectedConfig" class="p-4 border rounded-md bg-surface-50 text-sm text-surface-700">
        <h4 class="font-semibold mb-2 text-surface-800">Propriedades do tipo pré-definido:</h4>
        <div class="space-y-1">
          <p><strong>Nome:</strong> {{ selectedConfig.name }}</p>
          <p><strong>Descrição:</strong> {{ selectedConfig.description }}</p>
        </div>
      </div>
    </template>

    <!-- Own-type path (and edit mode): full fields, with an optional base type to derive from. -->
    <template v-if="isEditMode || mode === 'custom'">
      <div v-if="!isEditMode" class="space-y-1">
        <label for="parent_type">Basear-se em um dos seus tipos (opcional)</label>
        <Select id="parent_type" filter v-model="parentId"
          :options="groupedOwnedTypes" optionLabel="name" optionValue="id" optionGroupLabel="label"
          optionGroupChildren="items" placeholder="Nenhum" show-clear fluid>
          <!-- Render the label from the full list, not the filtered options: with optionValue set,
               the default label lookup searches only the currently-visible (filtered) options, so a
               no-match filter would blank out a valid selection. -->
          <template #value="slotProps">
            <span v-if="selectedParent">{{ selectedParent.name }}</span>
            <span v-else>{{ slotProps.placeholder }}</span>
          </template>
        </Select>
        <small>Opcional: crie uma variação a partir de um dos seus tipos existentes. A descrição e os tipos de entidade são pré-preenchidos, mas podem ser editados.</small>
      </div>

      <FormField name="name" v-slot="{ field, ...fieldProps }">
        <label for="name">Nome <span v-if="!isEditMode" class="text-red-500">*</span></label>
        <!-- The name is the type's identity and cannot change after creation, so it is read-only
             when editing (the backend rejects name changes regardless). -->
        <InputText id="name" v-bind="{ ...field, ...fieldProps }" :disabled="isEditMode" fluid />
        <small v-if="fieldProps.invalid" class="p-error">{{ fieldProps.error?.message }}</small>
        <small v-else-if="isEditMode">
          O nome não pode ser alterado após a criação.
        </small>
        <small v-else>
          O nome oficial do tipo de relacionamento (por exemplo, Cliente, Fornecedor).
        </small>
      </FormField>

      <FormField name="description" v-slot="{ field, ...fieldProps }">
        <label for="description">Descrição <span class="text-red-500">*</span></label>
        <Textarea id="description" v-bind="{ ...field, ...fieldProps }" rows="3" fluid />
        <small v-if="fieldProps.invalid" class="p-error">{{ fieldProps.error?.message }}</small>
        <small v-else>Uma breve explicação do que este tipo de relacionamento representa.</small>
      </FormField>

      <FormField name="entity_types" v-slot="{ field, ...fieldProps }">
          <label for="entity_types">Tipos de Entidade Aplicáveis <span class="text-red-500">*</span></label>
          <MultiSelect id="entity_types" v-bind="{ ...field, ...fieldProps }" :options="entityTypeOptions" optionLabel="label" optionValue="value" placeholder="Selecione os tipos de entidade aplicáveis" fluid />
          <small v-if="fieldProps.invalid" class="p-error">{{ fieldProps.error?.message }}</small>
          <small v-else>O tipo de entidades ao qual este relacionamento pode ser aplicado.</small>
      </FormField>

      <FormField name="active" v-slot="{ field, ...fieldProps }">
        <div class="flex items-center">
          <ToggleSwitch id="active" v-bind="{ ...field, ...fieldProps }" />
          <label for="active" class="ml-3">Ativo</label>
        </div>
        <small class="block mt-[-0.5rem] ml-[3.25rem] w-full whitespace-normal">
          Tipos inativos ficarão ocultos da seleção em outras partes do sistema.
        </small>
      </FormField>
    </template>

    <div class="flex justify-end gap-2 pt-4">
      <Button type="button" @click="emit('cancel')" label="Cancelar" severity="secondary" />
      <Button type="submit" :label="isEditMode ? 'Salvar Alterações' : 'Criar'" />
    </div>
  </Form>
</template>

<script setup>
import { computed, ref, watch } from 'vue'
import { Form, FormField } from '@primevue/forms'
import InputText from 'primevue/inputtext'
import Textarea from 'primevue/textarea'
import ToggleSwitch from 'primevue/toggleswitch'
import Select from 'primevue/select'
import SelectButton from 'primevue/selectbutton'
import Button from 'primevue/button'
import MultiSelect from 'primevue/multiselect';
import { useToast } from 'primevue/usetoast';
import { getEntityTypeLabel } from '@/utils/dataCatalogPresentation';

const props = defineProps({
  initialData: {
    type: Object,
    default: () => ({
      name: '',
      description: '',
      active: true,
      slug: null,
      config_type_id: null,
      entity_types: []
    })
  },
  configTypes: {
    type: Array,
    default: () => []
  },
  ownedTypes: {
    type: Array,
    default: () => []
  },
  options: {
      type: Object,
      required: true
  }
})

const emit = defineEmits(['submit', 'cancel'])
const toast = useToast();

const isEditMode = computed(() => !!props.initialData.id)

const entityTypeOptions = computed(() =>
  props.options.entity_types.map((entityType) => ({
    label: getEntityTypeLabel(entityType),
    value: entityType,
  }))
)

const modeOptions = [
  { label: 'Usar um tipo pré-definido', value: 'predefined' },
  { label: 'Criar um tipo próprio', value: 'custom' },
]
// Default to the pre-defined path when any are available to adopt, else straight to a custom type.
const mode = ref(props.configTypes.length ? 'predefined' : 'custom')

// Each lineage field has its own binding — no shared/encoded selection value.
const configId = ref(null)   // -> config_type_id (pre-defined path)
const parentId = ref(null)   // -> parent_relationship_type_id (own-type path, optional)
const relationshipForm = ref(null)

const selectedConfig = computed(() => props.configTypes.find((c) => c.id === configId.value) || null)
// Resolved against the full owned list so the parent select's label survives filtering (see #value).
const selectedParent = computed(() => props.ownedTypes.find((o) => o.id === parentId.value) || null)

// Split the owned types offered as a base into "basic" (adopted copies of a pre-defined type, which
// carry a base_type) and the account's own creations, so the parent select shows a labelled header
// separating the two. Groups with no members are omitted.
const groupedOwnedTypes = computed(() => {
  const basics = props.ownedTypes.filter((t) => t.base_type)
  const others = props.ownedTypes.filter((t) => !t.base_type)
  const groups = []
  if (basics.length) groups.push({ label: 'Tipos básicos', items: basics })
  if (others.length) groups.push({ label: 'Outros', items: others })
  return groups
})

// Keep the two lineage fields mutually exclusive by construction (matches the backend constraint):
// switching mode clears the other path's selection.
watch(mode, (newMode) => {
  if (newMode === 'predefined') {
    parentId.value = null
  } else {
    configId.value = null
  }
})

// Convenience when deriving a custom type from an existing one: seed the editable fields from the
// chosen base type (name is left for the user, since a variation usually gets its own name).
watch(parentId, (id) => {
  if (isEditMode.value || !relationshipForm.value) return
  const parent = props.ownedTypes.find((o) => o.id === id)
  if (parent) {
    relationshipForm.value.setFieldValue('description', parent.description)
    relationshipForm.value.setFieldValue('entity_types', parent.entity_types)
  }
})

const resolver = (values) => {
  const errors = {}
  // The typed fields only exist (and are only required) on the own-type / edit path.
  if (isEditMode.value || mode.value === 'custom') {
    if (!values.name?.trim()) {
      errors.name = { message: 'O nome é obrigatório' }
    }
    if (!values.description?.trim()) {
      errors.description = { message: 'A descrição é obrigatória' }
    }
    if (!values.entity_types || values.entity_types.length === 0) {
      errors.entity_types = { message: 'Pelo menos um tipo de entidade é obrigatório' }
    }
  }
  return { values, errors }
}

const handleSubmit = ({ values }) => {
  // Pre-defined path: the user adopts by reference only. We send just the config_type_id; the
  // backend copies name/description/entity types from the template, so we don't fabricate them here.
  if (!isEditMode.value && mode.value === 'predefined') {
    const config = selectedConfig.value
    if (!config) {
      toast.add({
        severity: 'error',
        summary: 'Erro de Validação',
        detail: 'Selecione um tipo pré-definido.',
        life: 3000
      });
      return
    }
    emit('submit', {
      active: true,
      config_type_id: config.id,
      parent_relationship_type_id: null,
    })
    return
  }

  // Own-type path (and edit): use the typed fields.
  const validationResult = resolver(values.values);
  if (Object.keys(validationResult.errors).length > 0) {
    toast.add({
      severity: 'error',
      summary: 'Erro de Validação',
      detail: 'Por favor, preencha todos os campos obrigatórios.',
      life: 3000
    });
    return;
  }

  const dataToSubmit = { ...validationResult.values };
  if (isEditMode.value) {
    // Name is immutable after creation; never send it on an edit.
    delete dataToSubmit.name;
  } else {
    dataToSubmit.config_type_id = null;
    dataToSubmit.parent_relationship_type_id = parentId.value || null;
  }
  emit('submit', dataToSubmit);
}
</script>

<style scoped>
/* Scoped styles for the form if needed */
</style>
